import { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { type UnsubscribeContactsApiArg } from '@/__generated/es-api'
import { createNotification } from '@/app/module/notifications/store/actions'
import { selectOrgId } from '@/app/module/user/store/selectors'
import { esApi, getErrorMessage } from '@/app/store/api'
import { useAppDispatch } from '@/app/store/redux-hooks'

import { getGroupSegmentLabel } from './group-segment-label'

export type UnsubscribeGroupsPayload = {
  groupIds: number[]
  isSegment: boolean
}

export const useUnsubscribeGroups = () => {
  const dispatch = useAppDispatch()
  const orgId = useSelector(selectOrgId)
  const [unsubscribeContacts, res] = esApi.endpoints.unsubscribeContacts.useMutation()

  const unsubscribeGroupsFromCampaign = useCallback(
    async (campaignId: number, payload: UnsubscribeGroupsPayload) => {
      if (payload.groupIds.length === 0) {
        return undefined
      }

      const body: UnsubscribeContactsApiArg['body'] = {
        contacts: {
          includedGroupIds: payload.groupIds,
        },
      }

      try {
        const result = await unsubscribeContacts({
          orgId: orgId.toString(),
          campaignId: campaignId.toString(),
          body,
        }).unwrap()

        dispatch(
          createNotification({
            'campaign-unsubscribe-groups': {
              type: 'success',
              message: `${getGroupSegmentLabel(payload.isSegment, payload.groupIds.length)} successfully unsubscribed from campaign.`,
            },
          }),
        )
        return result
      } catch (error) {
        const label = getGroupSegmentLabel(payload.isSegment, payload.groupIds.length).toLowerCase()
        dispatch(
          createNotification({
            'campaign-unsubscribe-groups-failure': {
              type: 'error',
              message: getErrorMessage(
                error,
                `A problem occurred with trying to unsubscribe the ${label} from the campaign. Please try again later.`,
              ),
            },
          }),
        )
        return undefined
      }
    },
    [dispatch, orgId, unsubscribeContacts],
  )

  return [unsubscribeGroupsFromCampaign, res] as const
}
